import type { LscMatch, LscNeonsportzImportCounts } from './types';
import { parseStageSortKey } from './leagueScheduleSort';

function fetchedGameCount(counts?: LscNeonsportzImportCounts): number | null {
  if (!counts) return null;
  const fetched = counts.fetched_games ?? counts.games;
  return fetched == null ? null : fetched;
}

/** Identical duplicate rows explain a lower distinct count than the provider's row count. */
export function identicalDuplicatesExplainCountGap(counts?: LscNeonsportzImportCounts): boolean {
  if (!counts || counts.games_truncated) return false;
  if (counts.schedule_fetch_complete === false) return false;
  const fetched = fetchedGameCount(counts);
  const providerTotal = counts.provider_total_games;
  const skipped = counts.duplicate_games_skipped ?? 0;
  if (fetched == null || providerTotal == null || skipped <= 0) return false;
  return fetched < providerTotal && fetched + skipped >= providerTotal;
}

export function formatNeonsportzGameCounts(counts?: LscNeonsportzImportCounts): string {
  if (!counts) return '—';
  const fetched = fetchedGameCount(counts);
  const providerTotal = counts.provider_total_games;
  if (identicalDuplicatesExplainCountGap(counts) && fetched != null && providerTotal != null) {
    const skipped = counts.duplicate_games_skipped ?? 0;
    return `${fetched} distinct games (${providerTotal} listed, ${skipped} identical duplicates)`;
  }
  if (providerTotal != null && fetched != null && providerTotal !== fetched) {
    return `${fetched} fetched of ${providerTotal} on NeonSportz`;
  }
  if (fetched != null) {
    return String(fetched);
  }
  return '—';
}

export function formatNeonsportzTeamCounts(counts?: LscNeonsportzImportCounts): string {
  if (!counts) return '—';
  const fetched = counts.fetched_teams ?? counts.teams;
  const providerTotal = counts.provider_total_teams;
  if (providerTotal != null && fetched != null && providerTotal !== fetched) {
    return `${fetched} fetched of ${providerTotal} on NeonSportz`;
  }
  if (fetched != null) {
    return String(fetched);
  }
  return '—';
}

export function isNeonsportzScheduleIncomplete(counts?: LscNeonsportzImportCounts): boolean {
  if (!counts) return false;
  if (counts.games_truncated) return true;
  if (counts.schedule_fetch_complete === false) return true;
  if (identicalDuplicatesExplainCountGap(counts)) return false;
  const fetched = fetchedGameCount(counts);
  const providerTotal = counts.provider_total_games;
  if (providerTotal != null && fetched != null && fetched < providerTotal) {
    return true;
  }
  return Boolean(counts.truncated && counts.games_truncated);
}

export function formatScheduleWeekRange(counts?: LscNeonsportzImportCounts): string | null {
  if (!counts) return null;
  const minWeek = counts.min_week_detected;
  const maxWeek = counts.max_week_detected;
  if (minWeek == null && maxWeek == null) return null;
  if (minWeek != null && maxWeek != null) {
    return `Week ${minWeek}–${maxWeek}`;
  }
  if (maxWeek != null) {
    return `through Week ${maxWeek}`;
  }
  return `from Week ${minWeek}`;
}

export function formatTruncationNote(counts?: LscNeonsportzImportCounts): string | null {
  if (identicalDuplicatesExplainCountGap(counts)) {
    const fetched = fetchedGameCount(counts);
    const providerTotal = counts?.provider_total_games;
    const skipped = counts?.duplicate_games_skipped ?? 0;
    const added = counts?.week_scoped_fetch?.added_games_count ?? 0;
    const addedSentence =
      added > 0
        ? ` Week-by-week checks added ${added} games the season list left out.`
        : '';
    return (
      `NeonSportz reported ${providerTotal} games. This preview will import ${fetched} ` +
      `distinct games. ${skipped} identical copies were skipped.${addedSentence}`
    );
  }
  if (isNeonsportzScheduleIncomplete(counts)) {
    const fetched = fetchedGameCount(counts);
    const providerTotal = counts?.provider_total_games;
    if (counts?.games_truncated) {
      return (
        `Import is blocked because the fetch stopped at the safety limit` +
        (fetched != null && providerTotal != null
          ? ` after ${fetched} of ${providerTotal} games NeonSportz reported.`
          : '.')
      );
    }
    if (fetched != null && providerTotal != null) {
      return (
        `Import is blocked because this preview retrieved ${fetched} games and NeonSportz ` +
        `reported ${providerTotal}. Preview again — a season still being posted can change ` +
        `while it is read.`
      );
    }
    return (
      'Import is blocked because this preview did not retrieve every game NeonSportz reported. ' +
      'Preview again.'
    );
  }
  if (!counts?.truncated) return null;
  const parts: string[] = [];
  if (counts.games_truncated) {
    parts.push('game list was truncated to the preview/import safety limit');
  }
  if (counts.teams_truncated) {
    parts.push('team list was truncated to the preview/import safety limit');
  }
  if (!parts.length) return null;
  return `Note: ${parts.join('; ')}. Imported data uses fetched counts, not display-only totals.`;
}

export function summarizeLeagueScheduleMatches(matches: LscMatch[]): {
  total: number;
  seasonLabel: string | null;
  minWeek: number | null;
  maxWeek: number | null;
  appearsIncomplete: boolean;
} {
  const weekValues = matches
    .map((match) => parseStageSortKey(match.stage_label))
    .filter((value) => value > 0 && value < 5000);
  const seasons = Array.from(
    new Set(matches.map((match) => (match.season_label ?? '').trim()).filter(Boolean))
  );
  const minWeek = weekValues.length ? Math.min(...weekValues) : null;
  const maxWeek = weekValues.length ? Math.max(...weekValues) : null;
  const appearsIncomplete = matches.length > 0 && maxWeek != null && maxWeek < 8;
  return {
    total: matches.length,
    seasonLabel: seasons.length === 1 ? seasons[0] : null,
    minWeek,
    maxWeek,
    appearsIncomplete,
  };
}
